import { AvailableLanguages } from 'i18nConfig';
import type {
  StandardModel,
  CollectionModel,
  EformModel,
} from 'redux/services/api.generated';
import { isEnumValue, typedEntries } from 'util/util';

interface ColumnSizeProps {
  width?: number;
  flex?: number;
  minWidth?: number;
  maxWidth?: number;
}

type ColumnSizeConfig<T extends string> = Partial<Record<T, ColumnSizeProps>>;

type CustomColumnField = 'actions';

type ModelColumnSizeConfig<
  T,
  C extends string = CustomColumnField,
> = ColumnSizeConfig<Extract<keyof T, string> | Exclude<C, keyof T>>;

export type StandardColumnSizeConfig = ModelColumnSizeConfig<StandardModel>;
export type CollectionColumnSizeConfig = ModelColumnSizeConfig<CollectionModel>;
export type EformColumnSizeConfig = ModelColumnSizeConfig<EformModel>;

export const defaultStandardColumnSizes = {
  name: { flex: 1, minWidth: 150 },
  sdo: { width: 150 },
  collectionName: { flex: 1, minWidth: 150 },
  dateAdded: { width: 120 },
  lineSource: { width: 150 },
  lineStatus: { flex: 0.5, minWidth: 260 },
  actions: { width: 150 },
} as const satisfies StandardColumnSizeConfig;

export const defaultCollectionColumnSizes: CollectionColumnSizeConfig = {
  name: { flex: 1, minWidth: 150 },
  lineStatus: { flex: 0.5, minWidth: 260 },
  dateAdded: { width: 120 },
  lineSource: { width: 150 },
  collectionStandardCount: { width: 150 },
  actions: { width: 150 },
} as const satisfies CollectionColumnSizeConfig;

export const defaultEformColumnSizes: EformColumnSizeConfig = {
  name: { flex: 1, minWidth: 150 },
  sdo: { width: 150 },
  dateAdded: { width: 120 },
  lineSource: { width: 150 },
  count: { width: 120 },
  lineStatus: { flex: 0.5, minWidth: 260 },
  actions: { width: 150 },
} as const satisfies EformColumnSizeConfig;

export const standardColumnSizeOverrides: Partial<
  Record<AvailableLanguages, StandardColumnSizeConfig>
> = { [AvailableLanguages.fi]: { actions: { width: 180 } } };

export const collectionColumnSizeOverrides: Partial<
  Record<AvailableLanguages, CollectionColumnSizeConfig>
> = { [AvailableLanguages.fi]: { actions: { width: 180 } } };

export const eformColumnSizeOverrides: Partial<
  Record<AvailableLanguages, EformColumnSizeConfig>
> = { [AvailableLanguages.fi]: { actions: { width: 180 } } };

export function resolveColumnSizes<T extends string>(
  defaults: ColumnSizeConfig<T>,
  language: string,
  overrides: Partial<Record<AvailableLanguages, ColumnSizeConfig<T>>>,
): ColumnSizeConfig<T> {
  if (!isEnumValue(AvailableLanguages, language)) {
    return { ...defaults };
  }

  const languageOverrides = overrides[language];
  if (!languageOverrides) {
    return { ...defaults };
  }

  const result: ColumnSizeConfig<T> = { ...defaults };

  typedEntries(languageOverrides).forEach(([key, sizes]) => {
    result[key] = { ...result[key], ...sizes };
  });

  return result;
}
